import React, { useState, useEffect } from 'react'
import omit from 'lodash/omit'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'

import { Accordion } from '@react/react-spectrum/Accordion'
import AccordionItem from '@react/react-spectrum/Accordion/js/AccordionItem'
import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Heading from '@react/react-spectrum/Heading'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Select from '@react/react-spectrum/Select'
import Switch from '@react/react-spectrum/Switch'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'
import Link from '@react/react-spectrum/Link'
import { SHARED_FIELDS } from '../../config/components/_shared'
import { getActionFlowVerticalPreset } from '../../config/components/vertical'
import Copy from '@react/react-spectrum/Icon/Copy'
import Delete from '@react/react-spectrum/Icon/Delete'
import Help from '@react/react-spectrum/Icon/Help'

import { CssPropertiesInput } from 'components/CssProperties'
import ColorInput from 'components/ColorInput'

import ComponentField from './ComponentField'

import { copyValue } from 'utils/clipboard'
import NumberInput from '@react/react-spectrum/NumberInput'

import AssetsGallery from '../AssetsGallery/AssetsGallery'
import { getImageField } from 'utils/editor'

const DOCUMENTATION_BASE_URL = 'https://dsn.adobe.com/docs/'
const CUSTOM_CLASS_TOOLTIP =
  'Space-separated list of CSS classes to add to the top-level element of this component. Can be used to identify component or link custom CSS branding.'
const CUSTOM_STYLE_TOOLTIP =
  'CSS styles to add to the top-level element of this component. Styles should be separated by new lines or semi-colons. Each style has to follow property: value format. See placeholder for details.'
const CUSTOM_STYLE_MOBILEV2_TOOLTIP =
  'Custom styles applied to the component. Due to limitations of mobile styling, those styles are only applied to the top-level element of this component. Properties should be written in camelCase, values should be written in kebab-case or as numbers. Examples: { justifyContent: flex-end } or { marginBottom: 12 }'

const ComponentSidebar = ({
  component,
  projectType,
  configs,
  onComponentDelete,
  onComponentSave,
  SidebarHeader,
  features,
  disabled,
}) => {
  const componentId = component?.meta?.id
  const componentConfig = configs && component?.meta?.component && configs[component.meta.component]

  const [isImportAssets, setIsImportAssets] = useState(false)

  useEffect(() => {
    // whenever a new component becomes active in the editor or on sidebar close, reset the import state
    setIsImportAssets(false)
  }, [componentId])

  // get the first field in componentConfig that has type 'image', flatten section fields
  const imageField = getImageField(componentConfig) //TODO: add support for multiple image fields in one component
  const isImageLike = !!imageField

  const onComponentContentSave = (propName) => (value) => {
    if (!disabled) {
      const nextContent = cloneDeep(component.content ?? {})
      set(nextContent, propName, value)
      if (
        component.meta?.component === 'actionFlow' &&
        propName === 'vertical' &&
        value !== component.content?.vertical
      ) {
        const { steps, attributes } = getActionFlowVerticalPreset(value, nextContent.attributes ?? {})
        nextContent.steps = steps
        nextContent.attributes = attributes
      }
      onComponentSave({ ...component, content: nextContent })
    }
  }

  const onComponentMetaSave = (propName) => (value) => {
    if (!disabled) {
      const nextMeta = cloneDeep(component.meta ?? {})
      set(nextMeta, propName, value)
      onComponentSave({ ...component, meta: nextMeta })
    }
  }

  const onComponentStylesSave = (styles) => {
    if (!disabled) onComponentSave({ ...component, styles })
  }

  const onComponentCopy = () => copyValue(component)

  const onSave =
    componentConfig?.hasChildren || componentConfig?.hasColumns || componentConfig?.isMeta
      ? onComponentMetaSave
      : onComponentContentSave

  const hiddenFilter = (field) =>
    (typeof field?.hidden === 'function' ? !field.hidden(component, features) : !field?.hidden) &&
    field.name !== '_columns'

  const columnControl = componentConfig?.fields?.find((field) => field.name === '_columns')

  const [componentFields, componentSections] = componentConfig?.fields?.filter(hiddenFilter)?.reduce(
    (acc, field) => {
      if (field.type === 'section') {
        acc[1][field.label] = field.fields
      } else {
        acc[0].push(field)
      }
      return acc
    },
    [[...SHARED_FIELDS], {}]
  ) || [[...SHARED_FIELDS], {}]

  const changeColumns = (n) => {
    if (!disabled) {
      let newContent = []

      // Create new columns with content copied from matching columns
      for (let i = 0; i < n; i++) {
        newContent.push([...(component.content[i] || [])])
      }

      // If new number of columns is less than current, copy content from extra columns to last column
      if (n < component.content.length) {
        for (let i = n; i < component.content.length; i++) {
          newContent[n - 1].push(...component.content[i])
        }
      }

      onComponentSave({ ...component, meta: { ...component.meta, _columns: n }, content: newContent })
    }
  }

  const getStyleTooltip = () => (projectType === 'mobile-v2' ? CUSTOM_STYLE_MOBILEV2_TOOLTIP : CUSTOM_STYLE_TOOLTIP)

  const renderImportCta = () => {
    if (!isImageLike || isImportAssets) return null
    return (
      <div className='ComponentSidebar__importCta'>
        <Button variant='primary' onClick={() => setIsImportAssets((prev) => !prev)} disabled={disabled}>
          Import image from Livedemos
        </Button>
      </div>
    )
  }

  return (
    <div className='ComponentSidebar' onMouseDown={(e) => e.stopPropagation()}>
      {SidebarHeader}

      {componentConfig && !component && (
        <div className='ComponentSidebar__placeholder'>
          <em>Select a component to edit</em>
        </div>
      )}

      {component && (
        <div className='ComponentSidebar__mainContent'>
          <div className='ComponentSidebar__tools'>
            <span className='ComponentSidebar__title'>
              <Heading size={2}>{componentConfig?.label || 'Unknown'}</Heading>
            </span>
            <Button quiet icon={<Copy size='S' />} onClick={onComponentCopy} />
            <Button
              variant='warning'
              quiet
              icon={<Delete size='S' />}
              onClick={onComponentDelete}
              disabled={disabled}
            />
          </div>
          {componentConfig?.docs && (
            <Link
              href={`${DOCUMENTATION_BASE_URL}${componentConfig.docs}`}
              target='_blank'
              className='ComponentSidebar__docs'
            >
              Component documentation
            </Link>
          )}
          {!isImportAssets && componentConfig && (
            <>
              {columnControl && (
                <FieldLabel label={columnControl.label}>
                  <Select
                    id='component-sidebar-cmp-_columns'
                    value={component.content.length || columnControl.value || 1}
                    options={columnControl.options}
                    onSelect={changeColumns}
                    disabled={disabled}
                  />
                </FieldLabel>
              )}
              {renderImportCta()}
              {componentFields.map((field) => (
                <ComponentField
                  projectType={projectType}
                  key={`${componentId}-${field['name']}`}
                  field={field}
                  component={component}
                  config={componentConfig}
                  onSave={field?.isMeta ? onComponentMetaSave : onSave}
                  disabled={disabled}
                />
              ))}
            </>
          )}
        </div>
      )}
      {isImportAssets && isImageLike && (
        <AssetsGallery
          componentId={componentId}
          imageFieldName={imageField?.name}
          onClose={() => setIsImportAssets(false)}
          onSubmit={(src) => {
            //add cache-busting timestamp to the URL stored in DB, so the browser loads the latest image version
            const cacheBustedUrl = `${src}${src.includes('?') ? '&' : '?'}v=${Date.now()}`
            onComponentContentSave(imageField?.name)(cacheBustedUrl)
            setIsImportAssets(false)
          }}
        />
      )}

      {!isImportAssets && (componentSections || componentConfig?.hasStyles) && (
        <Accordion>
          {componentSections &&
            Object.entries(componentSections).map(([name, fields]) => (
              <AccordionItem header={name} key={name}>
                {fields.filter(hiddenFilter).map((field, idx) => (
                  <ComponentField
                    projectType={projectType}
                    key={`${componentId}-${field['name'] || idx}`}
                    field={field}
                    component={component}
                    config={componentConfig}
                    onSave={field?.isMeta ? onComponentMetaSave : onSave}
                    disabled={disabled}
                  />
                ))}
              </AccordionItem>
            ))}
          {componentConfig?.hasStyles && (
            <AccordionItem header='Styles'>
              {!projectType.includes('mobile') && (
                <div className='ComponentField'>
                  <FieldLabel label={'Custom CSS Classes'}>
                    <Textfield
                      value={component?.styles?.customClasses || ''}
                      onChange={(customClasses) =>
                        onComponentStylesSave({ ...(component?.styles || {}), customClasses })
                      }
                      disabled={disabled}
                    />
                  </FieldLabel>
                  <OverlayTrigger placement='right'>
                    <Button className='tooltip-btn' variant='tool' icon={<Help size='XS' />} />
                    <Tooltip variant='info'>{CUSTOM_CLASS_TOOLTIP}</Tooltip>
                  </OverlayTrigger>
                </div>
              )}
              {Array.isArray(componentConfig.exposedStyles) &&
                componentConfig.exposedStyles.filter(hiddenFilter).map((field) => (
                  <div className='ComponentField' key={`${componentId}-${field.name}`}>
                    <FieldLabel label={field.type === 'switch' ? '' : field.label}>
                      <ExposedStylesInput
                        {...field}
                        disabled={disabled}
                        onChange={onComponentStylesSave}
                        componentStyles={component?.styles}
                      />
                    </FieldLabel>
                    {field.tooltip && (
                      <OverlayTrigger placement='right'>
                        <Button className='tooltip-btn' variant='tool' icon={<Help size='XS' />} />
                        <Tooltip variant='info'>{field.tooltip}</Tooltip>
                      </OverlayTrigger>
                    )}
                  </div>
                ))}
              <div className='ComponentField'>
                <FieldLabel label={'Custom Styles'}>
                  <CssPropertiesInput
                    label='Edit Styles'
                    fields={Object.entries(omit(component?.styles, 'customClasses')).map(([prop, value]) => ({
                      prop,
                      value,
                    }))}
                    onChange={(styles) =>
                      onComponentStylesSave({
                        customClasses: component?.styles?.customClasses,
                        ...styles.reduce((acc, { prop, value }) => ({ ...acc, [prop]: value }), {}),
                      })
                    }
                    disabled={disabled}
                  />
                </FieldLabel>
                <OverlayTrigger placement='right'>
                  <Button className='tooltip-btn' variant='tool' icon={<Help size='XS' />} />
                  <Tooltip variant='info'>{getStyleTooltip()}</Tooltip>
                </OverlayTrigger>
              </div>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </div>
  )
}

function ExposedStylesInput({
  name,
  placeholder,
  label,
  componentStyles,
  onChange,
  options,
  disabled,
  type,
  ...field
}) {
  const handleChange = (value) => onChange({ ...componentStyles, [name]: value })
  switch (type) {
    case 'switch':
      return (
        <Switch
          label={label}
          checked={!!componentStyles?.[name]}
          onChange={(v) => !disabled && handleChange(v)}
        />
      )
    case 'color':
      return (
        <ColorInput
          placeholder={placeholder || label}
          id={'component-sidebar-' + name}
          value={componentStyles?.[name] || ''}
          onChange={handleChange}
          disabled={disabled}
        />
      )
    case 'number':
      return (
        <NumberInput
          value={componentStyles?.[name] || typeof componentStyles?.[name] === 'number' ? componentStyles?.[name] : ''}
          min={field['min'] || 0}
          max={field['max'] || 30}
          step={field['step'] || 1}
          onChange={handleChange}
          disabled={disabled}
        />
      )
    case 'dropdown':
      return (
        <Select
          placeholder={field['label']}
          value={componentStyles?.[name]}
          options={options}
          onChange={handleChange}
          disabled={disabled}
        />
      )
    case 'text':
    default:
      return (
        <Textfield
          value={componentStyles?.[name]}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
        />
      )
  }
}

export default ComponentSidebar
